/*
 * @Copyright 2023-2026. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useRef, useState } from 'react';
import './style.css';
import * as Selector from '../../stores/selector';
import { useStore } from '../../stores/common.ts';
import { ProjectType, isBiomoleculeProject, isCrystalCoreProject } from '../../constants.ts';
import { ControlledMenu } from '@szhsin/react-menu';

// The four project-type menus carry ~165 KB of menu-item source between them
// (molecularModelingMenuItems alone is 53 KB) and reach into the chem library
// for element pickers. This wrapper mounts on every page as the app's root
// context-menu host, so importing all four statically put all of it in the
// entry chunk — to render nothing until someone right-clicks. They are lazy
// now, and gated on isOpen below so the fetch happens on first right-click
// rather than at load.
const DrugDiscoveryMenu = React.lazy(() => import('./drugDiscoveryDefaultMenu.tsx'));
const MolecularModelingMenu = React.lazy(() => import('./molecularModelingDefaultMenu.tsx'));
const CrystalBuilderMenu = React.lazy(() => import('./crystalBuilderDefaultMenu.tsx'));
const BiomoleculeMenu = React.lazy(() => import('./biomoleculeDefaultMenu.tsx'));

export interface ContextMenuProps {
  [key: string]: any;
}

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 12;

const DropdownContextMenu: React.FC<ContextMenuProps> = ({ children }) => {
  const projectType = useStore(Selector.projectType);

  const [isOpen, setOpen] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });

  // Long-press detection state. Touch devices don't fire `contextmenu` on a
  // tap, and React's bubbling can be interrupted by intermediate handlers
  // calling stopPropagation. Listen at the window level (capture phase) so
  // we never miss the gesture regardless of where it lands in the tree.
  const longPressTimerRef = useRef<number | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const cancelLongPress = () => {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      longPressStartRef.current = null;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        cancelLongPress();
        return;
      }
      const t = e.touches[0];
      // Skip subtrees that own their own long-press / context-menu UI.
      // The project gallery has per-card edit menus, so a long-press on a
      // gallery card must not open the lab's menu underneath.
      const target = t.target as Element | null;
      if (target && typeof target.closest === 'function' && target.closest('[data-suppress-chamber-context-menu]')) {
        return;
      }
      longPressStartRef.current = { x: t.clientX, y: t.clientY };
      longPressTimerRef.current = window.setTimeout(() => {
        if (longPressStartRef.current) {
          const { x, y } = longPressStartRef.current;
          // Synthesize a `contextmenu` event at the touch position so that
          // anything listening on the right-click path also fires for a
          // long-press (capture-phase atom picker in the chamber, etc.).
          // The bubble-phase onContextMenu handler below catches the
          // synthetic event and opens the menu via the normal path —
          // there's no need to call setOpen separately.
          const targetEl = (document.elementFromPoint(x, y) as Element | null) ?? document.body;
          const evt = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: x,
            clientY: y,
            button: 2,
          });
          targetEl.dispatchEvent(evt);
        }
        longPressTimerRef.current = null;
      }, LONG_PRESS_MS);
    };

    const onTouchMove = (e: TouchEvent) => {
      const start = longPressStartRef.current;
      if (!start || e.touches.length === 0) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (dx * dx + dy * dy > LONG_PRESS_MOVE_TOLERANCE_PX * LONG_PRESS_MOVE_TOLERANCE_PX) {
        cancelLongPress();
      }
    };

    // Capture phase + passive listeners so we observe touch events before
    // any element calls preventDefault / stopPropagation, but we don't
    // interfere with native scrolling.
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('touchstart', onTouchStart, opts);
    window.addEventListener('touchmove', onTouchMove, opts);
    window.addEventListener('touchend', cancelLongPress, opts);
    window.addEventListener('touchcancel', cancelLongPress, opts);
    return () => {
      window.removeEventListener('touchstart', onTouchStart, opts);
      window.removeEventListener('touchmove', onTouchMove, opts);
      window.removeEventListener('touchend', cancelLongPress, opts);
      window.removeEventListener('touchcancel', cancelLongPress, opts);
      cancelLongPress();
    };
  }, []);

  // Close-on-outside-tap. @szhsin/react-menu's own dismissal is blur-
  // based: when focus leaves its container, it closes. That works for
  // mouse/keyboard but breaks on touch — tapping a non-focusable element
  // like the R3F canvas doesn't move focus, so blur never fires and the
  // menu stays open. We listen at document level in the capture phase so
  // we get the tap before any child handler can stopPropagation, and
  // close manually when it landed outside the menu's DOM.
  //
  // pointerdown is the primary signal (unified for mouse / touch / pen),
  // with touchstart kept as a backstop on environments that suppress
  // pointerdown for the canvas. We check the event's own composedPath
  // (and target) rather than `elementFromPoint`, which sometimes returns
  // the wrong layer when the menu animates in or when the page is
  // pinch-zoomed (visual-viewport coordinates ≠ layout coordinates).
  useEffect(() => {
    if (!isOpen) return;

    const isInsideMenu = (e: Event): boolean => {
      // composedPath() includes the menu portal even when the original
      // target lookup wouldn't (e.g. inside shadow DOM).
      const path =
        typeof (e as { composedPath?: () => EventTarget[] }).composedPath === 'function'
          ? (e as { composedPath: () => EventTarget[] }).composedPath()
          : [];
      for (const node of path) {
        if (
          node instanceof Element &&
          (node.classList?.contains('szh-menu') || node.classList?.contains('szh-menu-container'))
        ) {
          return true;
        }
      }
      const target = e.target as Element | null;
      return !!(target && typeof target.closest === 'function' && target.closest('.szh-menu, .szh-menu-container'));
    };

    const onOutside = (e: Event) => {
      if (isInsideMenu(e)) return;
      setOpen(false);
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener('pointerdown', onOutside, opts);
    document.addEventListener('touchstart', onOutside, opts);
    document.addEventListener('mousedown', onOutside, opts);
    return () => {
      document.removeEventListener('pointerdown', onOutside, opts);
      document.removeEventListener('touchstart', onOutside, opts);
      document.removeEventListener('mousedown', onOutside, opts);
    };
  }, [isOpen]);

  const menus = () => {
    // Don't reference a lazy menu until the menu is actually open — that is
    // what defers the chunk fetch to the first right-click instead of mount.
    if (!isOpen) return null;
    if (projectType === ProjectType.DRUG_DISCOVERY) {
      return <DrugDiscoveryMenu />;
    } else if (isCrystalCoreProject(projectType)) {
      return <CrystalBuilderMenu />;
    } else if (isBiomoleculeProject(projectType)) {
      return <BiomoleculeMenu />;
    } else if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
      return <MolecularModelingMenu />;
    } else {
      return <MolecularModelingMenu />;
    }
  };

  return (
    <div
      onContextMenu={(e) => {
        if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
        e.preventDefault();
        setAnchorPoint({ x: e.clientX, y: e.clientY });
        setOpen(true);
      }}
    >
      {children}
      <ControlledMenu
        anchorPoint={anchorPoint}
        state={isOpen ? 'open' : 'closed'}
        onClose={() => {
          setOpen(false);
        }}
        menuStyle={{ fontSize: '14px', minWidth: '4rem', borderRadius: '0.35rem' }}
      >
        <React.Suspense fallback={null}>{menus()}</React.Suspense>
      </ControlledMenu>
    </div>
  );
};

export default DropdownContextMenu;
